import { useState, useRef, useEffect, useId } from 'react';
import { XIcon, CropIcon, GripIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
	type Modifier,
} from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../tooltip';
import { ImageEditor, type ImageEditorTexts } from './image-editor';
import { DropZone } from './drop-zone';

export type MultiImageItem = { type: 'existing'; url: string } | { type: 'new'; file: File };

function getItemKey(item: MultiImageItem): string {
	return item.type === 'existing' ? item.url : `file-${item.file.name}-${item.file.size}-${item.file.lastModified}`;
}

function getItemSrc(item: MultiImageItem, previewUrls: Map<File, string>): string {
	return item.type === 'existing' ? item.url : (previewUrls.get(item.file) ?? '');
}

export function MultiImageUpload(props: {
	id?: string;
	images: MultiImageItem[];
	onAddFiles: (files: File[]) => void;
	onRemove: (index: number) => void;
	onReorder: (fromIndex: number, toIndex: number) => void;
	onCrop: (index: number, croppedFile: File) => void;
	label: string;
	readOnly: boolean;
	maxImages?: number;
	renderImageExtra?: (index: number) => React.ReactNode;
	noImagesText?: string;
	cropImageText?: string;
	removeImageText?: string;
	dragToReorderText?: string;
	dropZoneDescription?: string;
	imageEditorTexts?: Partial<ImageEditorTexts>;
}) {
	// Track original files and crop areas for the image editor, keyed by current file reference
	const fileDataRef = useRef(
		new Map<File, { original: File; cropArea: { x: number; y: number; width: number; height: number } | null }>(),
	);
	const [editingIndex, setEditingIndex] = useState<number | null>(null);

	// Generate preview URLs for new files
	const previewUrlsRef = useRef(new Map<File, string>());
	const currentFiles = new Set<File>();
	for (const img of props.images) {
		if (img.type === 'new') {
			currentFiles.add(img.file);
			if (!previewUrlsRef.current.has(img.file)) {
				previewUrlsRef.current.set(img.file, URL.createObjectURL(img.file));
			}
			// Ensure file data is tracked
			if (!fileDataRef.current.has(img.file)) {
				fileDataRef.current.set(img.file, { original: img.file, cropArea: null });
			}
		}
	}
	// Cleanup stale preview URLs and file data
	for (const [file, url] of previewUrlsRef.current) {
		if (!currentFiles.has(file)) {
			URL.revokeObjectURL(url);
			previewUrlsRef.current.delete(file);
		}
	}
	for (const file of fileDataRef.current.keys()) {
		if (!currentFiles.has(file)) fileDataRef.current.delete(file);
	}
	useEffect(
		() => () => {
			for (const url of previewUrlsRef.current.values()) URL.revokeObjectURL(url);
		},
		[],
	);

	const hasReachedMax = props.maxImages != null && props.images.length >= props.maxImages;

	// DndContext uses an auto-incrementing counter for its accessibility `aria-describedby` ID
	// (e.g. "DndDescribedBy-0"). This counter can differ between server and client renders,
	// causing a hydration mismatch. Passing a stable `useId()` value fixes it.
	const dndId = useId();

	// DnD sensors with distance threshold to allow button clicks
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
	const itemKeys = props.images.map(getItemKey);

	// Restrict drag movement to the grid container so items don't overflow the hidden parent
	const gridRef = useRef<HTMLDivElement>(null);
	const restrictToGrid: Modifier = ({ transform, draggingNodeRect }) => {
		if (!draggingNodeRect || !gridRef.current) return transform;
		const rect = gridRef.current.getBoundingClientRect();
		return {
			...transform,
			x: Math.min(Math.max(transform.x, rect.left - draggingNodeRect.left), rect.right - draggingNodeRect.right),
			y: Math.min(Math.max(transform.y, rect.top - draggingNodeRect.top), rect.bottom - draggingNodeRect.bottom),
		};
	};
	const modifiers = [restrictToGrid];

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = itemKeys.indexOf(active.id as string);
		const newIndex = itemKeys.indexOf(over.id as string);
		if (oldIndex !== -1 && newIndex !== -1) {
			props.onReorder(oldIndex, newIndex);
		}
	};

	const editingItem = editingIndex !== null ? props.images[editingIndex] : null;
	const editingFile = editingItem?.type === 'new' ? editingItem.file : null;
	const editingFileData = editingFile ? fileDataRef.current.get(editingFile) : null;

	return (
		<div>
			{props.readOnly && props.images.length === 0 && (
				<p className="text-muted-foreground text-sm">{props.noImagesText ?? 'No images uploaded'}</p>
			)}
			<AnimatePresence initial={false}>
				{props.images.length > 0 && (
					<motion.div
						key="images"
						initial={{ height: 0 }}
						animate={{ height: 'auto' }}
						exit={{ height: 0 }}
						transition={{ type: 'spring', visualDuration: 0.2, bounce: 0 }}
						className="overflow-hidden"
					>
						<DndContext
							id={dndId}
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							modifiers={modifiers}
						>
							<SortableContext items={itemKeys} strategy={rectSortingStrategy}>
								<div ref={gridRef} className="mb-3 grid grid-cols-3 items-start gap-3">
									{props.images.map((item, i) => (
										<SortableImageCard
											key={getItemKey(item)}
											id={getItemKey(item)}
											src={getItemSrc(item, previewUrlsRef.current)}
											isExisting={item.type === 'existing'}
											readOnly={props.readOnly}
											cropImageText={props.cropImageText}
											removeImageText={props.removeImageText}
											dragToReorderText={props.dragToReorderText}
											onCrop={() => setEditingIndex(i)}
											onRemove={() => props.onRemove(i)}
											extra={props.renderImageExtra?.(i)}
										/>
									))}
								</div>
							</SortableContext>
						</DndContext>
					</motion.div>
				)}
			</AnimatePresence>
			<div
				className="grid"
				style={{
					gridTemplateRows: props.readOnly || hasReachedMax ? '0fr' : '1fr',
					opacity: props.readOnly || hasReachedMax ? 0 : 1,
					transition: 'grid-template-rows 200ms, opacity 200ms',
				}}
			>
				<div className="-m-1 overflow-hidden p-1">
					<DropZone
						id={props.id}
						label={props.label}
						description={props.dropZoneDescription}
						multiple
						onFiles={(newFiles) => {
							let imageFiles = newFiles.filter((f) => f.type.startsWith('image/'));
							if (props.maxImages != null) {
								const remaining = props.maxImages - props.images.length;
								imageFiles = imageFiles.slice(0, Math.max(0, remaining));
							}
							if (imageFiles.length > 0) props.onAddFiles(imageFiles);
						}}
					/>
				</div>
			</div>
			{editingIndex !== null && editingFile && (
				<ImageEditor
					open
					file={editingFileData?.original ?? editingFile}
					aspectRatio={{ width: 413, height: 286 }}
					initialCropArea={editingFileData?.cropArea || undefined}
					texts={props.imageEditorTexts}
					onSave={(croppedFile, newCropArea) => {
						const originalFile = editingFileData?.original ?? editingFile;
						// Update file data: remove old entry, add new one
						fileDataRef.current.delete(editingFile);
						fileDataRef.current.set(croppedFile, { original: originalFile, cropArea: newCropArea });
						props.onCrop(editingIndex, croppedFile);
						setEditingIndex(null);
					}}
					onOpenChange={(open) => {
						if (!open) setEditingIndex(null);
					}}
				/>
			)}
		</div>
	);
}

function SortableImageCard(props: {
	id: string;
	src: string;
	isExisting: boolean;
	readOnly: boolean;
	cropImageText?: string;
	removeImageText?: string;
	dragToReorderText?: string;
	onCrop: () => void;
	onRemove: () => void;
	extra?: React.ReactNode;
}) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.id });

	const style = {
		transform: CSS.Translate.toString(transform),
		transition: isDragging ? 'none' : transition,
		zIndex: isDragging ? 10 : 0,
	};

	return (
		<div ref={setNodeRef} style={style} className="flex flex-col gap-1">
			<div
				className={`relative shrink-0 overflow-hidden rounded-lg ${isDragging ? 'ring-primary/30 shadow-lg ring-2' : ''}`}
			>
				<img src={props.src} alt="" className="w-full object-cover" style={{ aspectRatio: '413 / 286' }} />
				{!props.readOnly && (
					<div className="absolute top-1 right-1 flex gap-1">
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type="button"
										aria-label={props.dragToReorderText ?? 'Drag to reorder'}
										className="cursor-grab rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70 active:cursor-grabbing"
										{...attributes}
										{...listeners}
									>
										<GripIcon className="size-3.5" />
									</button>
								</TooltipTrigger>
								<TooltipContent>{props.dragToReorderText ?? 'Drag to reorder'}</TooltipContent>
							</Tooltip>
							{!props.isExisting && (
								<Tooltip>
									<TooltipTrigger asChild>
										<button
											type="button"
											aria-label={props.cropImageText ?? 'Crop image'}
											onClick={props.onCrop}
											className="rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
										>
											<CropIcon className="size-3.5" />
										</button>
									</TooltipTrigger>
									<TooltipContent>{props.cropImageText ?? 'Crop image'}</TooltipContent>
								</Tooltip>
							)}
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type="button"
										aria-label={props.removeImageText ?? 'Remove image'}
										onClick={props.onRemove}
										className="rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
									>
										<XIcon className="size-3.5" />
									</button>
								</TooltipTrigger>
								<TooltipContent>{props.removeImageText ?? 'Remove image'}</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</div>
				)}
			</div>
			{props.extra}
		</div>
	);
}
